import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Save, Ticket, Trash2, X } from "lucide-react";
import { Btn, Section, TONES, inputCls } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";
import CouponCard from "@/components/mall/CouponCard";
import { themeOf } from "@/lib/mall";

const TONE = TONES.marketing;
const BLANK = { id: null, code: "", title: "", kind: "percent", value: 10, min_subtotal: 0, expires_at: "", terms: "" };
const STATUS = {
    pending: ["In review", "border-[#b54708] bg-[#fffaeb] text-[#b54708]"],
    active: ["Approved", "border-[#067647] bg-[#ecfdf3] text-[#067647]"],
    rejected: ["Sent back", "border-[#b42318] bg-[#fef3f2] text-[#b42318]"],
};

/** Codes a customer types at the register — these hang on 13thav-wide coupon book. */
export default function CouponsPanel({ onCount }) {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api.get("/store/coupons").then(({ data }) => { setData(data); onCount?.(data.coupons?.length || 0); })
            .catch(() => setData({ coupons: [], cap: 0 }));
    }, [onCount]);
    useEffect(() => { load(); }, [load]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const { data: res } = await api.post("/store/coupons", {
                ...form, value: Number(form.value) || 0, min_subtotal: Number(form.min_subtotal) || 0,
            });
            toast.success(res.note);
            setForm(BLANK);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const act = async (fn, msg) => {
        try { await fn(); if (msg) toast.success(msg); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-zinc-500" /></div>;

    if (!data.cap) return (
        <div className="rounded-lg border border-black/10 bg-white p-8 text-center" data-testid="coupons-locked">
            <Ticket size={28} className="mx-auto text-zinc-400" />
            <p className="mt-2.5 text-sm text-zinc-800">Coupons hang in front of a store. Open a plan and yours go in the wholesale coupon book.</p>
            <Link to="/account/sell" className="mt-3 inline-block"><GreenButton data-testid="coupons-upgrade">See the spaces</GreenButton></Link>
        </div>
    );

    const store = data.store;
    const preview = {
        ...form, code: form.code || "YOURCODE", title: form.title || "Your coupon headline",
        value_label: form.kind === "percent" ? `${Number(form.value) || 0}% OFF` : `$${Number(form.value) || 0} OFF`,
        min_subtotal: Number(form.min_subtotal) || 0,
        store: { ...(store || { slug: "", name: "Your shop" }), theme: themeOf(store) },
    };

    return (
        <div data-testid="coupons-panel">
            <p className="text-sm text-zinc-800">
                A coupon is a code the customer types at <span className="font-semibold">your</span> register only, and it
                hangs on 13thav-wide <Link to="/wholesale" className="font-semibold underline">coupon book</Link>.
                Editing one sends it back to the directory office for a look — the on/off switch doesn't.
                <span className="ml-1 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-900">
                    {data.coupons.length} of {data.cap} printed
                </span>
            </p>

            <div className="mt-3">
                {data.coupons.length ? (
                    <Section tone={TONE} pad={false} testid="my-coupons" title={`${data.coupons.length} coupons`}
                        note="Switched off keeps it in the book but the code stops working">
                        {data.coupons.map((c) => {
                            const [label, cls] = STATUS[c.status] || STATUS.pending;
                            return (
                                <div key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-black/10 px-3 py-2.5 first:border-t-0"
                                    data-testid={`coupon-row-${c.code}`}>
                                    <span className="font-mono text-sm font-black tracking-[0.12em] text-zinc-900">{c.code}</span>
                                    <div className="min-w-[160px] flex-1">
                                        <p className="text-sm font-semibold text-zinc-900">{c.title}</p>
                                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-700">
                                            {c.value_label} · {c.min_subtotal > 0 ? `$${c.min_subtotal} minimum` : "no minimum"}
                                            {c.expires_at ? ` · through ${c.expires_at}` : ""} · used {c.redemptions || 0}×
                                        </p>
                                        {c.review_note && <p className="text-xs text-red-700">{c.review_note}</p>}
                                    </div>
                                    <span className={`rounded border px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] ${cls}`}
                                        data-testid={`coupon-status-${c.code}`}>{label}</span>
                                    <span className={`rounded border px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] ${c.active
                                        ? "border-[#067647] bg-[#ecfdf3] text-[#067647]" : "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]"}`}
                                        data-testid={`coupon-switch-${c.code}`}>{c.active ? "on" : "off"}</span>
                                    <Btn testid={`coupon-toggle-${c.code}`}
                                        onClick={() => act(() => api.post(`/store/coupons/${c.id}/toggle`), "Switch flipped")}>
                                        {c.active ? <><EyeOff size={11} /> Switch off</> : <><Eye size={11} /> Switch on</>}
                                    </Btn>
                                    <Btn testid={`coupon-edit-${c.code}`} onClick={() => setForm({ ...BLANK, ...c })}>Edit</Btn>
                                    <button onClick={() => act(() => api.delete(`/store/coupons/${c.id}`), "Coupon torn up")}
                                        data-testid={`coupon-delete-${c.code}`}
                                        className="text-zinc-600 transition-colors hover:text-red-600"><Trash2 size={14} /></button>
                                </div>
                            );
                        })}
                    </Section>
                ) : (
                    <p className="rounded-lg border border-dashed border-black/15 bg-white p-6 text-center text-sm text-zinc-800"
                        data-testid="coupons-empty">
                        No coupons printed yet. Write your first one below.
                    </p>
                )}
            </div>

            <form onSubmit={save} className="mt-4" data-testid="coupon-form">
                <Section tone={TONE} title={form.id ? `Edit ${form.code}` : "Print a coupon"}
                    note="Percent off or dollars off, with an optional bag minimum"
                    right={form.id ? (
                        <Btn testid="coupon-cancel" onClick={() => setForm(BLANK)}><X size={11} /> new coupon instead</Btn>
                    ) : null}>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block">
                            <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Code</span>
                            <input className={`${inputCls} font-mono uppercase`} placeholder="SUMMER10" value={form.code}
                                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} data-testid="coupon-code" />
                        </label>
                        <label className="block">
                            <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Headline</span>
                            <input className={inputCls} placeholder="10% off your bag" value={form.title} onChange={set("title")} data-testid="coupon-title" />
                        </label>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-4">
                        <label className="block">
                            <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Type</span>
                            <select className={inputCls} value={form.kind} onChange={set("kind")} data-testid="coupon-kind">
                                <option value="percent">Percent off</option>
                                <option value="amount">Dollars off</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">
                                {form.kind === "percent" ? "Percent" : "Dollars"}
                            </span>
                            <input className={inputCls} type="number" min="1" step="1" value={form.value} onChange={set("value")} data-testid="coupon-value" />
                        </label>
                        <label className="block">
                            <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Bag minimum</span>
                            <input className={inputCls} type="number" min="0" step="5" value={form.min_subtotal} onChange={set("min_subtotal")} data-testid="coupon-min" />
                        </label>
                        <label className="block">
                            <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Good through</span>
                            <input className={inputCls} type="date" value={form.expires_at || ""} onChange={set("expires_at")} data-testid="coupon-expires" />
                        </label>
                    </div>
                    <label className="mt-3 block">
                        <span className="mb-1 block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Fine print</span>
                        <input className={inputCls} placeholder="One per customer. Not on rush jobs."
                            value={form.terms || ""} onChange={set("terms")} data-testid="coupon-terms" />
                    </label>
                    <div className="mt-4 flex flex-wrap items-start gap-5 border-t border-black/10 pt-3">
                        <GreenButton type="submit" disabled={busy} data-testid="coupon-save">
                            {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                            {form.id ? "Save changes" : "Send to 13thav support"}
                        </GreenButton>
                        <div className="min-w-[260px] flex-1">
                            <p className="mb-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-700">
                                Preview — your ticket in the book
                            </p>
                            <div className="max-w-md" data-testid="coupon-preview"><CouponCard coupon={preview} /></div>
                        </div>
                    </div>
                </Section>
            </form>
        </div>
    );
}
